// Fork from https://github.com/react-hook-form/resolvers/blob/master/joi/src/joi.ts

import { toNestError, validateFieldsNatively } from '@hookform/resolvers';
import type { Resolver } from '@hookform/resolvers/joi';
import type {
  AsyncValidationOptions,
  Context,
  Reference,
  ValidationError,
} from 'joi';
import type { FieldError } from 'react-hook-form';
import { appendErrors } from 'react-hook-form';
import { JsonPrefix } from './data';

type FieldErrorWithContext = FieldError & { context?: Context };
type Labels = { [key: string]: string };
type ResolverOptions = {
  mode?: 'async' | 'sync';
  labels?: Labels;
};

const getRefLabel = (maybeRef: Reference, labels?: Labels) => {
  return labels?.[maybeRef.key] ?? maybeRef.key;
};

const transformRefsToReadableText = (
  originalContext: Context | undefined,
  newContext: Context,
  labels?: Labels,
) => {
  // if ref value rendering is false we might get values like this
  // ref:a, ref:global:b, ref:local:c, ref:...d in the new context.
  // we will try to transform it to something useful, maybe the field label it self,
  // if the form labels is provided to the resolver.
  const overrides = Object.entries(newContext)
    .filter(([_, val]) => val?.startsWith?.('ref:'))
    .map(([key]) => [key, originalContext?.[key]])
    .reduce((acc, [key, refWrapper]) => {
      if (Array.isArray(refWrapper))
        return {
          ...acc,
          [key]: refWrapper.map((ref) => getRefLabel(ref, labels)),
        };
      return { ...acc, [key]: getRefLabel(refWrapper, labels) };
    }, {});

  return { ...newContext, ...overrides };
};

const resolveErrorData = (
  error: FieldErrorWithContext,
  labels?: Labels,
): FieldErrorWithContext => {
  const data = { ...error };
  const context = error.context;
  const message = error.message;

  if (message?.startsWith(JsonPrefix)) {
    const newContext = JSON.parse(message.replace('JSON#', ''));
    data.context = transformRefsToReadableText(context, newContext, labels);
    data.message = data.type;
  }
  return data;
};

const parseErrorSchema = (
  error: ValidationError,
  validateAllFieldCriteria: boolean,
  labels?: Labels,
) =>
  error.details.length
    ? error.details.reduce<Record<string, FieldErrorWithContext>>(
        (previous, error) => {
          const _path = error.path.join('.');
          if (!previous[_path]) {
            previous[_path] = resolveErrorData(error, labels);
          }

          if (validateAllFieldCriteria) {
            const types = previous[_path].types;
            const messages = types && types[error.type!];

            previous[_path] = appendErrors(
              _path,
              validateAllFieldCriteria,
              previous,
              error.type,
              messages
                ? ([] as string[]).concat(messages as string[], error.message)
                : error.message,
            ) as FieldErrorWithContext;
          }

          return previous;
        },
        {},
      )
    : {};

const joiResolver =
  (
    schema: any,
    schemaOptions: AsyncValidationOptions = {
      abortEarly: false,
    },
    resolverOptions: ResolverOptions = {},
  ): ReturnType<Resolver> =>
  async (values, context, options) => {
    const _schemaOptions = Object.assign({}, schemaOptions, {
      context,
    });

    let result: Record<string, any> = {};
    if (resolverOptions.mode === 'sync') {
      result = schema.validate(values, _schemaOptions);
    } else {
      try {
        result.value = await schema.validateAsync(values, _schemaOptions);
      } catch (e) {
        result.error = e;
      }
    }

    if (result.error) {
      return {
        values: {},
        errors: toNestError(
          parseErrorSchema(
            result.error,
            !options.shouldUseNativeValidation &&
              options.criteriaMode === 'all',
            resolverOptions.labels,
          ),
          options,
        ),
      };
    }

    options.shouldUseNativeValidation && validateFieldsNatively({}, options);

    return {
      errors: {},
      values: result.value,
    };
  };

export default joiResolver;
